import {
  Input,
  Output,
  inject,
  OnInit,
  Component,
  DestroyRef,
  EventEmitter,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { LoggerService } from '@services/logger.service';
import { Principal } from '../../../../core/models/object.models';

import { NomObjectClass } from '../../../../core/constants';
import { ObjectDefinition } from '../../../../core/models/core.models';
import { ChildPopupPaneComponent } from '../../../../core/models/form.models';

import {
  clearSelectedPrincipals,
  togglePrincipalSelection,
  toggleAllPrincipalSelection,
} from '../../../../core/state/principals.actions';

import { selectUser, selectPricipalsSelectedMap } from '../../../../core/state';
import { AppState } from 'src/app/core/state/app.state';

import {
  tap,
  map,
  skip,
  take,
  Observable,
  BehaviorSubject,
  distinctUntilChanged,
  finalize,
  switchMap,
} from 'rxjs';
import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';
import { NgpRovingFocusGroup } from 'ng-primitives/roving-focus';
import { NgpToolbar } from 'ng-primitives/toolbar';
import { SuiToolbarComponent } from '@sui/toolbar';
import { SearchInputComponent } from '../../../../shared/components/search-input/search-input.component';
import { SuiCheckboxComponent } from '@sui/checkbox';
import { LoadingComponent } from '../../../../shared/components/loading/loading.component';
import { PrincipalCardComponent } from '../../../../shared/components/cards/principal-card/principal-card.component';
import { SuiButtonComponent } from '@sui/button';
import { SuiPaginationComponent } from '@sui/pagination';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-principal-object-list',
  templateUrl: './principal-object-list.component.html',
  styleUrl: './principal-object-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgpRovingFocusGroup,
    NgpToolbar,
    SuiToolbarComponent,
    SearchInputComponent,
    SuiCheckboxComponent,
    LoadingComponent,
    PrincipalCardComponent,
    SuiButtonComponent,
    SuiPaginationComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class PrincipalObjectListComponent
  implements OnInit, ChildPopupPaneComponent
{
  @Input() companyId: string | null | undefined = undefined;
  @Input() singleSelectionActive: boolean = false;
  @Input() selectedObjectIds: Map<string, ObjectDefinition>;
  /** Principal ids the backend leaves out of the list (e.g. the current owner in the owner picker). */
  @Input() lockedUserIds: string[] = [];
  @Input() searchString$: BehaviorSubject<string> = new BehaviorSubject<string>(
    '',
  );
  @Input() objectClassIds: Array<number> = [
    NomObjectClass.USER,
    NomObjectClass.USER_GROUP,
  ];

  @Output() formGroupChange = new EventEmitter<FormGroup>();
  @Output() componentDataChange: EventEmitter<Map<string, ObjectDefinition>> =
    new EventEmitter();

  temporalSelectedMembersMap$: Observable<Map<string, Principal>>;
  temporalSelectedMembersList$: Observable<Array<Principal>>;

  membersPerPage: Array<Principal>;
  membersLoading: boolean = false;
  membersTotal: number;
  temporalSelectedMembersMap: Map<string, Principal> = new Map<
    string,
    Principal
  >();
  NomObjectClass = NomObjectClass;

  // * Pagination data
  currentPage = 1;
  pageSize = 30;

  private destroyRef = inject(DestroyRef);
  private logger = inject(LoggerService);
  private utilsDataObjectApi = inject(UtilsDataObjectApiService);
  private crd = inject(ChangeDetectorRef);

  protected selectAllChecked = false;

  formGroup: FormGroup = new FormGroup({});

  constructor(private store: Store<AppState>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['objectClassIds']) {
      this.logger.debug(
        'PrincipalObjectListComponent',
        'ngOnChanges',
        'objectClassIds changed, reloading principals',
        changes['objectClassIds'].currentValue,
      );
      // reload principals with new objectClassIds
      this.initPicker();
    }
  }

  ngOnInit(): void {
    this.initPicker();
  }

  initPicker(): void {
    if (this.companyId) {
      this.membersLoading = true;
      this.fetchPrincipals(
        this.companyId,
        this.currentPage,
        this.searchString$?.value ?? '',
        this.objectClassIds,
      );
    } else {
      this.store
        .select(selectUser)
        .pipe(
          take(1),
          tap((user) => {
            this.companyId = user.active_company_id;
            this.membersLoading = true;
          }),
          switchMap((user) =>
            this.utilsDataObjectApi.fetchCompanyPrincipals(
              user.active_company_id,
              this.currentPage,
              this.searchString$?.value ?? '',
              this.objectClassIds,
              this.lockedUserIds,
            ),
          ),
          tap((response: any) => {
            this.membersPerPage = response.items;
            this.membersTotal = response.total;
          }),
          finalize(() => {
            this.membersLoading = false;
            this.crd.markForCheck();
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    // Combine the searchString$ with any other relevant inputs
    this.temporalSelectedMembersMap$ = this.store.select(
      selectPricipalsSelectedMap,
    );
    this.temporalSelectedMembersList$ = this.temporalSelectedMembersMap$.pipe(
      map((principalMap) => Array.from(principalMap.values())),
    );

    // 2) When searchString$ changes (already debounced by app-search-input),
    //    reset to page 1 and load with that query. An empty query is a real
    //    change too: clearing the box (backspace or the x) must reload the
    //    unfiltered list, otherwise the last term's results stay on screen.
    this.searchString$
      .pipe(
        skip(1), // Skip the initial value
        distinctUntilChanged(),
        tap((query) => {
          this.currentPage = 1;
          this.membersLoading = true;
        }),
        switchMap((query) =>
          this.utilsDataObjectApi
            .fetchCompanyPrincipals(
              this.companyId,
              this.currentPage,
              query,
              this.objectClassIds,
              this.lockedUserIds,
            )
            .pipe(
              finalize(() => {
                this.membersLoading = false;
                this.crd.markForCheck();
              }),
            ),
        ),
        tap((response: any) => {
          this.membersPerPage = response.items;
          this.membersTotal = response.total;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.temporalSelectedMembersMap$
      .pipe(
        tap((principalMap) => {
          this.temporalSelectedMembersMap = principalMap;

          const objectDefinitionMap: Map<string, ObjectDefinition> = new Map(
            Array.from(principalMap.entries()).map(([id, principal]) => [
              id,
              {
                object_id: principal.id,
                object_class_id: principal.object_class_id,
              },
            ]),
          );

          this.componentDataChange.emit(objectDefinitionMap);
          this.syncFormGroup();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  handleInputPrincipalSearchChange(searchString: string) {
    this.searchString$.next(searchString);
  }

  toggleAllSelected(selected: boolean) {
    this.logger.debug(
      'PrincipalObjectListComponent',
      'toggleAllSelected',
      'Toggling all selected principals',
    );

    this.store.dispatch(
      toggleAllPrincipalSelection({
        principals: this.membersPerPage,
        selected: selected,
      }),
    );
  }

  onSelectAllChange(selected: boolean) {
    this.selectAllChecked = selected;
    this.toggleAllSelected(selected);
  }

  onPageChange(page: number | string) {
    if (typeof page === 'string') {
      page = parseInt(page, 10) || 1;
    }

    this.selectAllChecked = false;

    this.currentPage = page;
    this.membersLoading = true;
    this.fetchPrincipals(
      this.companyId,
      this.currentPage,
      this.searchString$?.value ?? '',
      this.objectClassIds,
    );
  }

  syncFormGroup() {
    this.logger.debug(
      'PrincipalObjectListComponent',
      'syncFormGroup',
      'Syncing form group with selected principals',
    );
    // Clear existing controls
    this.formGroup = new FormGroup({});
    this.logger.debug(
      'PrincipalObjectListComponent',
      'syncFormGroup',
      'Cleared existing form group controls',
    );
    // Add controls for each selected principal
    this.addSelectedPrincipalsToFormGroup();
    this.logger.debug(
      'PrincipalObjectListComponent',
      'syncFormGroup',
      'Added selected principals to form group',
    );
    // Emit the updated form group
    this.formGroupChange.emit(this.formGroup);
    this.logger.debug(
      'PrincipalObjectListComponent',
      'syncFormGroup',
      'Emitted updated form group',
    );
  }

  addSelectedPrincipalsToFormGroup() {
    this.temporalSelectedMembersMap.forEach((principalObjectDef) => {
      if (principalObjectDef) {
        this.formGroup.addControl(
          `permission[${principalObjectDef.object_class_id}][${principalObjectDef.object_id}]`,
          new FormControl(true),
        );
        this.logger.debug(
          'PrincipalObjectListComponent',
          'addSelectedPrincipalsToFormGroup',
          `Added control for object_id: ${principalObjectDef.object_id}, object_class_id: ${principalObjectDef.object_class_id}`,
        );
      }
    });
    this.formGroupChange.emit(this.formGroup);
    this.logger.debug(
      'PrincipalObjectListComponent',
      'addSelectedPrincipalsToFormGroup',
      'Form group updated with selected principals',
    );
  }

  toggleSelection(principal: Principal) {
    this.store.dispatch(
      togglePrincipalSelection({
        principal,
        singleSelection: this.singleSelectionActive,
      }),
    );
  }

  isSelected(object: Principal): boolean {
    return this.temporalSelectedMembersMap.has(object.id);
  }

  get principalSelectedCount(): number {
    return this.temporalSelectedMembersMap.size;
  }

  clearSelection() {
    this.logger.debug(
      'PrincipalObjectListComponent',
      'clearSelection',
      'Clearing all selected principals',
    );
    this.store.dispatch(
      clearSelectedPrincipals({
        companyId: this.companyId,
      }),
    );
  }

  fetchPrincipals(
    companyId: string,
    currentPage: number,
    query: string,
    objectClassIds: NomObjectClass[],
  ) {
    this.utilsDataObjectApi
      .fetchCompanyPrincipals(
        companyId,
        currentPage,
        query,
        objectClassIds,
        this.lockedUserIds,
      )
      .pipe(
        tap((response) => {
          this.membersPerPage = response.items;
          this.membersTotal = response.total;
        }),
        finalize(() => {
          this.membersLoading = false;
          this.crd.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    // this.store.dispatch(
    //   clearSelectedPrincipals({
    //     companyId: this.companyId,
    //   }),
    // );
  }

  trackByPrincipalId(index: number, principal: Principal): string {
    return principal.id;
  }
}
